import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const TIER_COLORS = { free: "#60B8E8", professional: "#7AC860", executive: "#F5C842", enterprise: "#E8A020" };
const ROLES = ["owner", "admin", "member", "viewer"];

export default function AdminOrgs() {
  const [orgs, setOrgs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", tier: "professional", domain: "" });
  const [newMember, setNewMember] = useState({ user_id: "", role: "member" });
  const [newTeam, setNewTeam] = useState({ name: "" });
  const [newInvite, setNewInvite] = useState({ email: "", role: "member" });
  const [toast, setToast] = useState("");

  const loadAll = async () => {
    const [o, s] = await Promise.all([adminApi.orgs(100), adminApi.orgsSummary()]);
    setOrgs(o.data?.orgs || []);
    setSummary(s.data || null);
    if ((o.data?.orgs || []).length && !active) {
      openOrg(o.data.orgs[0]);
    }
  };

  const openOrg = async (org) => {
    setActive(org);
    const id = org.id || org._id;
    const [m, t, i] = await Promise.all([
      adminApi.orgMembers(id),
      adminApi.orgTeams(id),
      adminApi.orgInvites(id),
    ]);
    setMembers(m.data?.members || []);
    setTeams(t.data?.teams || []);
    setInvites(i.data?.invites || []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 4000); };

  const createOrg = async () => {
    if (!newOrg.name) return;
    setCreating(true);
    try {
      await adminApi.createOrg(newOrg);
      setNewOrg({ name: "", tier: "professional", domain: "" });
      flash(`Org "${newOrg.name}" created`);
      await loadAll();
    } catch (e) {
      flash(`Failed to create org`);
    } finally { setCreating(false); }
  };

  const addMember = async () => {
    if (!active || !newMember.user_id) return;
    try {
      await adminApi.addOrgMember(active.id || active._id, newMember);
      setNewMember({ user_id: "", role: "member" });
      flash("Member added");
      openOrg(active);
    } catch { flash("Failed to add member"); }
  };

  const removeMember = async (uid) => {
    try {
      await adminApi.removeOrgMember(active.id || active._id, uid);
      flash("Member removed");
      openOrg(active);
    } catch { flash("Failed to remove member"); }
  };

  const createTeam = async () => {
    if (!active || !newTeam.name) return;
    try {
      await adminApi.createOrgTeam(active.id || active._id, newTeam);
      setNewTeam({ name: "" });
      flash("Team created");
      openOrg(active);
    } catch { flash("Failed to create team"); }
  };

  const deleteTeam = async (id) => {
    try { await adminApi.deleteOrgTeam(id); flash("Team deleted"); openOrg(active); }
    catch { flash("Failed to delete team"); }
  };

  const sendInvite = async () => {
    if (!active || !newInvite.email) return;
    try {
      await adminApi.createOrgInvite(active.id || active._id, newInvite);
      setNewInvite({ email: "", role: "member" });
      flash("Invite created");
      openOrg(active);
    } catch { flash("Failed to create invite"); }
  };

  const revokeInvite = async (id) => {
    try { await adminApi.revokeOrgInvite(id); flash("Invite revoked"); openOrg(active); }
    catch { flash("Failed to revoke invite"); }
  };

  return (
    <div className="admin-page mesh-shell" data-testid="admin-orgs">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      <div className="admin-page__head">
        <h2 className="admin-page__title">Organizations &amp; Teams</h2>
        <div className="mesh-eyebrow">v13 · Multi-tenant workspaces · Members · Teams · Invites</div>
      </div>

      {toast && <div className="mesh-toast" data-testid="orgs-toast">{toast}</div>}

      {summary && (
        <section className="admin-section mesh-section" data-testid="orgs-summary">
          <h3 className="admin-section__title">Overview</h3>
          <div className="mesh-exec-grid">
            <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{summary.total_orgs}</div><div className="mesh-exec-cell__lbl">Orgs</div></div>
            <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{summary.total_members}</div><div className="mesh-exec-cell__lbl">Members</div></div>
            <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{summary.total_teams}</div><div className="mesh-exec-cell__lbl">Teams</div></div>
            <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{summary.pending_invites}</div><div className="mesh-exec-cell__lbl">Pending Invites</div></div>
          </div>
        </section>
      )}

      <section className="admin-section mesh-section" data-testid="orgs-create">
        <h3 className="admin-section__title">Create Organization</h3>
        <div className="admin-filters">
          <input type="text" placeholder="Org name" value={newOrg.name} onChange={(e) => setNewOrg((v) => ({ ...v, name: e.target.value }))} data-testid="new-org-name" />
          <select value={newOrg.tier} onChange={(e) => setNewOrg((v) => ({ ...v, tier: e.target.value }))} data-testid="new-org-tier">
            {["free","professional","executive","enterprise"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="text" placeholder="Domain (optional)" value={newOrg.domain} onChange={(e) => setNewOrg((v) => ({ ...v, domain: e.target.value }))} data-testid="new-org-domain" />
          <button type="button" className="admin-linkbtn" disabled={creating || !newOrg.name} onClick={createOrg} data-testid="new-org-submit">{creating ? "Creating…" : "Create"}</button>
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="orgs-list">
        <h3 className="admin-section__title">Organizations</h3>
        <div className="mesh-service-grid">
          {orgs.map((o) => {
            const isActive = (active?.id || active?._id) === (o.id || o._id);
            return (
              <article key={o.id || o._id} className={`mesh-service-card ${isActive ? "is-active" : ""}`}
                       style={{ borderColor: TIER_COLORS[o.tier] || "#7AC860" }}
                       onClick={() => openOrg(o)}
                       data-testid={`org-card-${(o.id||o._id).slice(0,8)}`}>
                <header className="mesh-service-card__head">
                  <span className="mesh-pill" style={{ borderColor: TIER_COLORS[o.tier], color: TIER_COLORS[o.tier] }}>{(o.tier || "free").toUpperCase()}</span>
                  {o.domain && <span className="mesh-pill mesh-pill--ghost">{o.domain}</span>}
                </header>
                <h3 className="mesh-service-card__title">{o.name}</h3>
                <p className="mesh-service-card__sum">Owner: {o.owner_user_id} · Created {o.created_at?.slice(0, 10)}</p>
              </article>
            );
          })}
          {orgs.length === 0 && <div className="admin-foot-note">No organizations yet. Create the first above.</div>}
        </div>
      </section>

      {active && (
        <>
          <section className="admin-section mesh-section" data-testid="orgs-members">
            <h3 className="admin-section__title">{active.name} · Members ({members.length})</h3>
            <div className="admin-filters">
              <input type="text" placeholder="user_id or email" value={newMember.user_id} onChange={(e) => setNewMember((v) => ({ ...v, user_id: e.target.value }))} data-testid="new-member-uid" />
              <select value={newMember.role} onChange={(e) => setNewMember((v) => ({ ...v, role: e.target.value }))} data-testid="new-member-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="button" className="admin-linkbtn" disabled={!newMember.user_id} onClick={addMember} data-testid="new-member-add">Add Member</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>User</th><th>Role</th><th>Since</th><th>Action</th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id || m._id}>
                    <td>{m.user_id}</td>
                    <td><span className="mesh-pill mesh-pill--ghost">{m.role}</span></td>
                    <td>{m.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td><button type="button" className="admin-linkbtn mesh-btn--danger" disabled={m.role === "owner"} onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>Remove</button></td>
                  </tr>
                ))}
                {members.length === 0 && <tr><td colSpan={4} className="admin-foot-note">No members.</td></tr>}
              </tbody>
            </table>
          </section>

          <section className="admin-section mesh-section" data-testid="orgs-teams">
            <h3 className="admin-section__title">{active.name} · Teams ({teams.length})</h3>
            <div className="admin-filters">
              <input type="text" placeholder="team name" value={newTeam.name} onChange={(e) => setNewTeam({ name: e.target.value })} data-testid="new-team-name" />
              <button type="button" className="admin-linkbtn" disabled={!newTeam.name} onClick={createTeam} data-testid="new-team-create">Create Team</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>Team</th><th>Lead</th><th>Created</th><th>Action</th></tr></thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id || t._id}>
                    <td>{t.name}</td>
                    <td>{t.lead_user_id || "—"}</td>
                    <td>{t.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td><button type="button" className="admin-linkbtn mesh-btn--danger" onClick={() => deleteTeam(t.id || t._id)} data-testid={`team-del-${(t.id||t._id).slice(0,8)}`}>Delete</button></td>
                  </tr>
                ))}
                {teams.length === 0 && <tr><td colSpan={4} className="admin-foot-note">No teams.</td></tr>}
              </tbody>
            </table>
          </section>

          <section className="admin-section mesh-section" data-testid="orgs-invites">
            <h3 className="admin-section__title">{active.name} · Invites ({invites.length})</h3>
            <div className="admin-filters">
              <input type="email" placeholder="invitee email" value={newInvite.email} onChange={(e) => setNewInvite((v) => ({ ...v, email: e.target.value }))} data-testid="new-invite-email" />
              <select value={newInvite.role} onChange={(e) => setNewInvite((v) => ({ ...v, role: e.target.value }))} data-testid="new-invite-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="button" className="admin-linkbtn" disabled={!newInvite.email} onClick={sendInvite} data-testid="new-invite-send">Send Invite</button>
            </div>
            <table className="admin-table">
              <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Token</th><th>Action</th></tr></thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id || i._id}>
                    <td>{i.email}</td>
                    <td><span className="mesh-pill mesh-pill--ghost">{i.role}</span></td>
                    <td><span className={`mesh-pill ${i.status === "accepted" ? "mesh-pill--gold" : "mesh-pill--ghost"}`}>{i.status}</span></td>
                    <td><code style={{ fontSize: 10 }}>{i.token?.slice(0, 24)}…</code></td>
                    <td><button type="button" className="admin-linkbtn mesh-btn--danger" disabled={i.status !== "pending"} onClick={() => revokeInvite(i.id || i._id)} data-testid={`invite-revoke-${(i.id||i._id).slice(0,8)}`}>Revoke</button></td>
                  </tr>
                ))}
                {invites.length === 0 && <tr><td colSpan={5} className="admin-foot-note">No invites.</td></tr>}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
